/**
 * Generatore Attestati — stato admin (localStorage demo) + sync pubblico sigilli.
 */
import { PRODOTTI_CERTIFICATI_SEED } from "./borgoCertificazioneData";
import { PREMIUM_META } from "./borgoPremiumData";
import { enrichCertificazioneOspitalita } from "../utils/ratingFinale";

const STORAGE_STATI = "6amico_certificato_admin_stati";
const STORAGE_PUBLIC_PRODOTTI = "6amico_certificato_pubblici_extra";
const STORAGE_OSPITALITA = "6amico_certificazione_ospitalita_overlay";
const ADMIN_SELEZIONATO = "Amministratore Unico 6Amico";

export const CERTIFICATO_ADMIN_EVENT = "6amico-certificato-admin-updated";

/** @type {Record<string, import('../types/borgoData').CertificatoStatoAdmin>} */
const SEED_STATI = {
  "prod-castiglione-evo-vallata": {
    prodottoOStrutturaId: "prod-castiglione-evo-vallata",
    votoIspettoreAssegnato: 8,
    giudizioSartorialeTesto: "",
    garanziaLegaleTesto: "",
    isApprovato: false,
    statoSpedizione: "In Elaborazione",
  },
  "struttura-castiglione-hotel-valle": {
    prodottoOStrutturaId: "struttura-castiglione-hotel-valle",
    votoIspettoreAssegnato: 7,
    giudizioSartorialeTesto: "Struttura ricettiva con vista Vallata — candidata a sigillo ospitalità.",
    garanziaLegaleTesto: "",
    isApprovato: false,
    statoSpedizione: "In Elaborazione",
  },
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emitUpdate() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CERTIFICATO_ADMIN_EVENT));
  }
}

function readJson(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

/** @returns {Record<string, import('../types/borgoData').CertificatoStatoAdmin>} */
function loadStatiMap() {
  const stored = readJson(STORAGE_STATI, {});
  return { ...SEED_STATI, ...stored };
}

function saveStatiMap(map) {
  writeJson(STORAGE_STATI, map);
  emitUpdate();
}

function slugifyId(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function mapCategoriaNegozio(cat) {
  if (cat === "Artigianato" || cat === "Oleificio") return "Artigianato Artistico Storico";
  return "Enogastronomia d'Élite";
}

function protocolPrefix(tipo, categoria) {
  if (tipo === "struttura") return "OSP";
  if (categoria === "Artigianato Artistico Storico") return "ART";
  return "ENOG";
}

function nextProtocol(tipo, categoria) {
  const year = new Date().getFullYear();
  const n = Math.floor(1000 + Math.random() * 8999);
  return `6A-${protocolPrefix(tipo, categoria)}-${year}-${n}`;
}

/**
 * @typedef {'prodotto'|'struttura'|'negozio'} CertificatoCandidateTipo
 * @typedef {{ prodottoOStrutturaId: string, tipo: CertificatoCandidateTipo, borgoSlug: string, nome: string, produttore: string, categoria?: string, prezzoRiferimento?: string, fotoProdotto?: string, qrParlanteUrl?: string, eCommerceDiretto?: string, strutturaTipo?: string }} CertificatoCandidate
 */

/** @returns {CertificatoCandidate[]} */
export function listCertificatoCandidates() {
  /** @type {CertificatoCandidate[]} */
  const out = [];
  const approvedIds = new Set(getPublicProdottiCertificati().map((p) => p.id));

  for (const [borgoSlug, meta] of Object.entries(PREMIUM_META)) {
    for (const s of meta.struttureOspitalita || []) {
      if (!s.isPremium) continue;
      const id = `struttura-${borgoSlug}-${slugifyId(s.nome)}`;
      const hasCert = s.certificazioneOspitalita || getOspitalitaOverlay(id);
      if (!hasCert) {
        out.push({
          prodottoOStrutturaId: id,
          tipo: "struttura",
          borgoSlug,
          nome: s.nome,
          produttore: `${s.tipo} · ${borgoSlug}`,
          strutturaTipo: s.tipo,
          eCommerceDiretto: s.linkPrenotazione,
        });
      }
    }

    for (const [negozioNome, ov] of Object.entries(meta.negoziPremium || {})) {
      if (!ov?.isPremium || !ov.premiumData?.inventarioProdotti?.length) continue;
      for (const item of ov.premiumData.inventarioProdotti) {
        const id = `prod-${borgoSlug}-${item.id}`;
        if (approvedIds.has(id) || approvedIds.has(`cert-${item.id}`)) continue;
        const alreadyPublic = getPublicProdottiCertificati().some(
          (p) => p.borgoSlug === borgoSlug && p.nome.includes(item.nome)
        );
        if (alreadyPublic) continue;
        out.push({
          prodottoOStrutturaId: id,
          tipo: "prodotto",
          borgoSlug,
          nome: item.nome,
          produttore: negozioNome,
          categoria: mapCategoriaNegozio("Enogastronomia"),
          prezzoRiferimento: item.prezzo ? `€ ${item.prezzo}` : "",
          fotoProdotto: item.foto,
          qrParlanteUrl: ov.premiumData.qrCodeParlanteUrl || `/qr/${item.id}`,
          eCommerceDiretto: `/prodotti?comune=${borgoSlug}`,
        });
      }
    }
  }

  const seen = new Set(out.map((c) => c.prodottoOStrutturaId));
  for (const id of Object.keys(SEED_STATI)) {
    if (!seen.has(id)) {
      const seed = SEED_STATI[id];
      out.push({
        prodottoOStrutturaId: id,
        tipo: id.startsWith("struttura-") ? "struttura" : "prodotto",
        borgoSlug: id.includes("castiglione") ? "castiglione-messer-raimondo" : "campli",
        nome: id.includes("evo") ? "EVO Vallata 750ml — candidatura" : "Hotel Valle del Fino — candidatura",
        produttore: id.includes("evo") ? "Frantoio della Vallata" : "Hotel Valle del Fino",
        categoria: "Enogastronomia d'Élite",
        prezzoRiferimento: "€ 18",
        fotoProdotto: "/images/prodotti/olio-vallata.jpg",
        qrParlanteUrl: "/qr/frantoio-vallata",
        eCommerceDiretto: "/prodotti?comune=castiglione-messer-raimondo",
      });
    }
  }

  return out;
}

/** @param {string} id */
export function getCertificatoCandidate(id) {
  return listCertificatoCandidates().find((c) => c.prodottoOStrutturaId === id) || null;
}

/** @param {string} id */
export function getCertificatoStato(id) {
  const map = loadStatiMap();
  if (map[id]) return { ...map[id] };
  return {
    prodottoOStrutturaId: id,
    votoIspettoreAssegnato: 0,
    giudizioSartorialeTesto: "",
    garanziaLegaleTesto: "",
    isApprovato: false,
    statoSpedizione: "In Elaborazione",
  };
}

/** @param {import('../types/borgoData').CertificatoStatoAdmin} stato */
export function saveCertificatoStato(stato) {
  const map = loadStatiMap();
  map[stato.prodottoOStrutturaId] = { ...stato };
  saveStatiMap(map);
  return stato;
}

/** @param {import('../types/borgoData').CertificatoStatoAdmin} stato */
export function isCertificatoStatoCompleto(stato) {
  const v = Number(stato.votoIspettoreAssegnato);
  return (
    v >= 1 &&
    v <= 10 &&
    String(stato.giudizioSartorialeTesto || "").trim().length > 10 &&
    String(stato.garanziaLegaleTesto || "").trim().length > 10
  );
}

function readExtraProdotti() {
  return readJson(STORAGE_PUBLIC_PRODOTTI, []);
}

function writeExtraProdotti(list) {
  writeJson(STORAGE_PUBLIC_PRODOTTI, list);
  emitUpdate();
}

/** Lista pubblica: seed + approvazioni admin */
export function getPublicProdottiCertificati() {
  const extra = readExtraProdotti();
  const byId = new Map();
  for (const p of PRODOTTI_CERTIFICATI_SEED) byId.set(p.id, p);
  for (const p of extra) byId.set(p.id, p);
  return [...byId.values()];
}

/** @param {string} id */
export function getOspitalitaOverlay(id) {
  const map = readJson(STORAGE_OSPITALITA, {});
  return map[id] || null;
}

function saveOspitalitaOverlay(id, cert) {
  const map = readJson(STORAGE_OSPITALITA, {});
  map[id] = cert;
  writeJson(STORAGE_OSPITALITA, map);
  emitUpdate();
}

/**
 * @param {CertificatoCandidate} candidate
 * @param {import('../types/borgoData').CertificatoStatoAdmin} stato
 */
function buildProdottoPubblico(candidate, stato) {
  const data = stato.dataEmissione || todayIso();
  const categoria = candidate.categoria || "Enogastronomia d'Élite";
  const certId = candidate.prodottoOStrutturaId.startsWith("prod-")
    ? `cert-${candidate.prodottoOStrutturaId.replace(/^prod-[^-]+-/, "")}`
    : `cert-${slugifyId(candidate.nome)}`;

  return {
    id: certId,
    borgoSlug: candidate.borgoSlug,
    nome: candidate.nome,
    produttore: candidate.produttore,
    categoria,
    prezzoRiferimento: candidate.prezzoRiferimento || "—",
    fotoProdotto: candidate.fotoProdotto || "/images/prodotti/olio-evo.jpg",
    qrParlanteUrl: candidate.qrParlanteUrl || `/qr/${certId}`,
    eCommerceDiretto: candidate.eCommerceDiretto || `/prodotti?comune=${candidate.borgoSlug}`,
    ratingEsemplare: {
      numeroProtocollo: nextProtocol("prodotto", categoria),
      dataApprovazione: data,
      selezionatoDa: ADMIN_SELEZIONATO,
      valutazioneVoto: Number(stato.votoIspettoreAssegnato),
      giudizioSartoriale: stato.giudizioSartorialeTesto,
      garanziaAutenticita: stato.garanziaLegaleTesto,
      enteScientificoSupporto: "Panel qualità 6Amico · emissione Amministratore Unico",
    },
  };
}

/**
 * @param {CertificatoCandidate} candidate
 * @param {import('../types/borgoData').CertificatoStatoAdmin} stato
 */
function buildOspitalitaPubblica(candidate, stato) {
  const data = stato.dataEmissione || todayIso();
  return enrichCertificazioneOspitalita({
    numeroIspezione: nextProtocol("struttura", null),
    dataIspezione: data,
    votoIspettore: Number(stato.votoIspettoreAssegnato),
    giudizioSartoriale: stato.giudizioSartorialeTesto,
    garanziaSosta6Amico: stato.garanziaLegaleTesto,
    votiDellePersone: [],
  });
}

/** @param {string} id */
export function approveAndSend(id) {
  const stato = getCertificatoStato(id);
  const candidate = getCertificatoCandidate(id);
  if (!candidate) throw new Error("Candidato non trovato");
  if (!isCertificatoStatoCompleto(stato)) throw new Error("Compila tutti i campi obbligatori (voto 1–10, giudizio, garanzia)");

  const updated = {
    ...stato,
    isApprovato: true,
    statoSpedizione: "Approvato & Inviato",
    dataEmissione: todayIso(),
  };
  saveCertificatoStato(updated);

  if (candidate.tipo === "struttura") {
    const cert = buildOspitalitaPubblica(candidate, updated);
    saveOspitalitaOverlay(id, cert);
    return { tipo: "struttura", certificazioneOspitalita: cert, stato: updated };
  }

  const prodotto = buildProdottoPubblico(candidate, updated);
  const extra = readExtraProdotti().filter((p) => p.id !== prodotto.id);
  extra.push(prodotto);
  writeExtraProdotti(extra);
  return { tipo: "prodotto", prodotto, stato: updated };
}

/**
 * Applica overlay certificazioni ospitalità da admin su strutture/negozi merged.
 * @param {object[]} strutture
 * @param {object[]} negozi
 */
export function applyAdminOspitalitaOverlays(strutture = [], negozi = []) {
  const struttureOut = strutture.map((s, idx) => {
    const slugGuess = s.linkPrenotazione?.match(/borgo=([^&]+)/)?.[1];
    const id = slugGuess
      ? `struttura-${slugGuess}-${slugifyId(s.nome)}`
      : `struttura-unknown-${slugifyId(s.nome)}-${idx}`;
    const overlay = getOspitalitaOverlay(id);
    if (!overlay) return s;
    return {
      ...s,
      certificazioneOspitalita: overlay,
      premiumData: s.premiumData
        ? { ...s.premiumData, certificazioneOspitalita: overlay }
        : s.premiumData,
    };
  });

  const negoziOut = negozi.map((n, idx) => {
    const id = `negozio-${slugifyId(n.nome)}-${idx}`;
    const overlay = getOspitalitaOverlay(id);
    if (!overlay) return n;
    return {
      ...n,
      certificazioneOspitalita: overlay,
      premiumData: n.premiumData ? { ...n.premiumData, certificazioneOspitalita: overlay } : undefined,
    };
  });

  return { struttureOspitalita: struttureOut, reteNegozi: negoziOut };
}
